import { Linking, Platform } from 'react-native';
import { showLocation } from 'react-native-map-link';

import i18n from '@/constants/i18n';

import { Logger } from './logging';

export const getDefaultMapUrl = (
  latitude: number | string,
  longitude: number | string,
  placeName?: string,
  googlePlaceId?: string,
) => {
  const queryParams = new URLSearchParams();
  queryParams.set('api', '1');

  // place_id is a precision hint, not a requirement — many shops don't have
  // one synced (it's tied to the opt-in Google Reviews setting). Search by
  // name whenever we have it so we still land on the business listing
  // instead of a bare coordinate pin; only fall back to raw coordinates when
  // there's no name to search by either.
  if (googlePlaceId) {
    queryParams.set('place_id', googlePlaceId);
  }
  queryParams.set('query', placeName || `${latitude},${longitude}`);

  return `https://www.google.com/maps/search/?${queryParams.toString()}`;
};

export const getDirectionsUrl = (
  latitude: number | string,
  longitude: number | string,
  name?: string,
  googlePlaceId?: string,
) => {
  const latLng = `${latitude},${longitude}`;

  const scheme = Platform.select({
    ios: 'maps:0,0?q=',
    android: 'geo:0,0?q=',
  });

  const url = Platform.select({
    ios: `${scheme}${latLng}`,
    android: `${scheme}${latLng}`,
    web: getDefaultMapUrl(latitude, longitude, name, googlePlaceId),
  });

  return url || '';
};

export async function openDirections(address: {
  latitude: number;
  longitude: number;
  locale: string;
  name?: string;
  googlePlaceId?: string;
}) {
  try {
    if (Platform.OS === 'web') {
      await Linking.openURL(
        getDirectionsUrl(
          address.latitude,
          address.longitude,
          address.name,
          address.googlePlaceId,
        ),
      );
      return;
    }
    const t = i18n.getFixedT(address.locale, 'common', 'shared.openDirections');

    await showLocation({
      latitude: address.latitude,
      longitude: address.longitude,
      title: address.name,
      dialogTitle: t('title'),
      dialogMessage: t('description'),
      cancelText: t('cancel'),
      appsBlackList: [
        'uber',
        'lyft',
        'grab',
        'gojek',
        'grabtaxi',
        'grabfood',
        'grabcar',
      ],
    });
  } catch (error) {
    Logger.error('Failed to open directions:', { error });

    // If library fail try to open using deep-link
    await Linking.openURL(
      getDirectionsUrl(address.latitude, address.longitude),
    );

    throw error;
  }
}
